/// <amd-module name="Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettingsView" />
import * as React from "react";
import { useTfsProject } from "Tfs/Platform/TfsComponent";
import { useObservable } from "VSS/Core/Observable";
import { usePageContext } from "VSS/Platform/Layout";
import { IListItemDetails, List, ListItem, ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { SingleLayerMasterPanel } from "VSSUI/MasterDetails";
import { bindSelectionToObservable } from "VSSUI/MasterDetailsContext";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider, IItemProvider } from "VSSUI/Utilities/Provider";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import { BoardFieldSettings, IBoardFieldSettings } from "Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettings";
import { FieldSettingsView } from "Wit/Common/WitSettingsViews/Components/FieldSettingsView";
import * as Resources from "Wit/Common/WitSettingsViews/Resources";
import { IFieldSettingsViewProps } from "Wit/Common/WitSettingsViewsTypes/Components/FieldSettingsView.props";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { getIconDataObservable } from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";

export function getBoardFieldSettingsTab(
    boardFieldSettings: BoardFieldSettings,
    additionalCoreField?: IFieldReference,
    disabled?: boolean,
    groupId?: string,
    onSaveSettings?: () => Promise<void>
): ISettingsView {
    return {
        groupId,
        isDirty: boardFieldSettings.isDirty,
        isValid: boardFieldSettings.isValid,
        name: Resources.FieldSettingsTab,
        onSaveSettings,
        renderContent: () => (
            <BoardFieldSettingsView boardFieldSettings={boardFieldSettings} disabled={disabled} additionalCoreField={additionalCoreField} />
        )
    };
}

export interface IFieldReference {
    id: number;
    referenceName: string;
}

interface IBoardFieldSettingsViewProps {
    boardFieldSettings: IBoardFieldSettings;
    disabled?: boolean;
    additionalCoreField?: IFieldReference;
}

/**
 * Customizable field settings view that allows for a list of core fields than can be selected,
 * then configurable additional fields that can be chosen from a supplied list.
 */
const BoardFieldSettingsView: React.FunctionComponent<IBoardFieldSettingsViewProps> = ({ boardFieldSettings, disabled, additionalCoreField }) => {
    const pageContext = usePageContext();
    const projectId = useTfsProject()?.id ?? "";
    const workItemTypes = boardFieldSettings.workItemTypes;
    const itemProvider = new ArrayItemProvider(workItemTypes.map(type => ({ id: type, text: type } as IListBoxItem)));
    const selection = React.useRef(new ListSelection());
    const [selectedItem] = useObservable(itemProvider.value[0]);
    // best effort cache to prevent loading flicker when clicking on work item type tab that has been loaded
    const workItemTypeDataMap = React.useRef(new Map<string, number[]>());

    const loadWorkItemTypeFields = async (workItemType: string): Promise<number[] | undefined> => {
        const workItemTypeService = pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
        try {
            const workItemTypeData = await workItemTypeService.getWorkItemTypeData(projectId, [workItemType]);
            if (workItemTypeData.length) {
                const typeFields = workItemTypeData[0].fields;
                workItemTypeDataMap.current.set(workItemType, typeFields);
                if (additionalCoreField && typeFields.some(fieldId => fieldId === additionalCoreField.id)) {
                    boardFieldSettings.addCoreFieldForType(workItemType, additionalCoreField.referenceName);
                }

                return typeFields;
            }
        } catch (e) {
            if (e?.message) {
                boardFieldSettings.setErrorMessage(workItemType, e.message);
            }
        }
    };

    const renderListItem = (index: number, item: IListBoxItem, details: IListItemDetails<IListBoxItem>, key?: string): JSX.Element | null => {
        const iconData = getIconDataObservable(projectId, item.id, pageContext);
        const isValid = boardFieldSettings.getIsValid(item.id);
        return (
            <ListItem key={key || "list-item" + index + item.id} index={index} details={details}>
                <div className="flex-row flex-grow flex-center h-scroll-hidden padding-vertical-8 padding-right-8 padding-left-16">
                    <WorkItemTypeIcon className="icon-margin" data={iconData} />
                    <Tooltip overflowOnly>
                        <span className="primary-text text-ellipsis flex-grow">{item.text}</span>
                    </Tooltip>
                    <Observer isValid={isValid}>
                        {() => (!isValid?.value ? <Status {...Statuses.Failed} key="error" size={StatusSize.m} /> : null)}
                    </Observer>
                </div>
            </ListItem>
        );
    };

    const renderMasterContent = (selection: ListSelection, itemProvider: IItemProvider<IListBoxItem>): JSX.Element => {
        return <List itemProvider={itemProvider} selection={selection} renderRow={renderListItem} width="100%" singleClickActivation={true} />;
    };

    const renderFieldSettingsForType = (type: string) => {
        const fieldSettings = boardFieldSettings.getFieldSettings(type);
        const errorMessage = boardFieldSettings.getErrorMessage(type);
        if (!fieldSettings || !errorMessage) {
            return null;
        }

        const fieldIds = workItemTypeDataMap.current.get(type);
        return (
            <WorkItemTypeFieldSettingsView
                description={Resources.FieldSettingsDescription}
                disabled={disabled}
                errorMessage={errorMessage}
                fieldIds={fieldIds}
                key={type}
                labels={{ showEmptyFields: Resources.ShowEmptyFields }}
                loadWorkItemTypeFields={() => loadWorkItemTypeFields(type)}
                onSettingsChanged={fieldSettings => {
                    boardFieldSettings.updateFieldSettings(type, fieldSettings);
                }}
                {...fieldSettings}
            />
        );
    };

    React.useEffect(() => {
        bindSelectionToObservable(selection.current, itemProvider, selectedItem);
    }, []);

    return (
        <div className="flex-row flex-grow">
            <SingleLayerMasterPanel
                className="wit-settings-master-panel flex-column"
                renderContent={() => renderMasterContent(selection.current, itemProvider)}
                showOnSmallScreens={true}
            />
            <Observer selectedItem={selectedItem}>{() => renderFieldSettingsForType(selectedItem.value.id)}</Observer>
        </div>
    );
};

interface IWorkItemTypeFieldSettingsViewProps extends IFieldSettingsViewProps {
    fieldIds?: number[];
    loadWorkItemTypeFields: () => Promise<number[] | undefined>;
}

const WorkItemTypeFieldSettingsView: React.FunctionComponent<IWorkItemTypeFieldSettingsViewProps> = ({
    fieldIds,
    loadWorkItemTypeFields,
    ...props
}) => {
    const [fieldsForType, setFieldsForType] = React.useState<number[] | undefined>(fieldIds);
    const updateFieldsForType = async () => {
        const typeFields = await loadWorkItemTypeFields();
        setFieldsForType(typeFields);
    };

    React.useEffect(() => {
        if (!fieldsForType) {
            updateFieldsForType();
        }
    }, []);

    return fieldsForType?.length ?? 0 > 0 ? <FieldSettingsView workItemTypeFields={fieldsForType} {...props} /> : <Spinner className="flex-grow" />;
};
